/**
 * Stable per-origin device identity, persisted in localStorage.
 *
 * The v3 sync log keys events by `(hlc, device)`, and fork detection needs a
 * device id that survives reloads and is shared by all tabs of this browser —
 * otherwise every reload would look like a brand-new writer. Two tabs on one
 * origin share the id; their concurrent edits still fork (same-device
 * divergence), so no data is lost either way.
 */
const STORAGE_KEY = 'jnote.deviceId';

let cached: string | null = null;

/** Lazily resolves the device id; returns '' when storage is unavailable. */
export function deviceId(): string {
  if (cached !== null) return cached;
  let id = '';
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored && /^[A-Za-z0-9_-]{1,32}$/.test(stored)) {
      id = stored;
    } else {
      // crypto.randomUUID is 128-bit hex; an 8-char slice is plenty for a
      // handful of browsers and keeps the key readable in the log.
      const suffix =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
          : Math.random().toString(16).slice(2, 12);
      id = `web-${suffix}`;
      globalThis.localStorage?.setItem(STORAGE_KEY, id);
    }
  } catch {
    id = '';
  }
  cached = id;
  return id;
}
